import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Camera, ChevronLeft, ChevronRight, MapPin, Calendar,
  DollarSign, Users, Briefcase, PlusCircle, CheckCircle2,
  X, Send, Sparkles
} from "lucide-react";
import {
  getPublicListings,
  getPublicProfiles,
  getOpenRequirements,
  applyToRequirement,
  MarketplaceProfile,
  MarketplaceRequirement,
} from "../../../Redux/ApiCalls/Dashboard/MarketplaceAPI";
import { formatINR, formatDate, checkProfileCompleteness, isAccountFrozen } from "./MarketplaceShared";
import { showSuccessToast, showErrorToast } from "../../atoms/Utlis/Toast";
import { resolveImage } from "../../../Utils/helper";
import sendFirebaseEvent from "../../../analytics/FirebaseAnalytics";
import { mpTrack } from "../../../analytics/MarketplaceTracker";

import buySellRentIcon from "../../../assets/images/marketplace/buy_sell_rent.svg";
import verifiedProfessionalsIcon from "../../../assets/images/marketplace/verified_professionals.svg";
import securePaymentsIcon from "../../../assets/images/marketplace/secure_payments.svg";
import discoverStepIcon from "../../../assets/images/marketplace/discover.svg";
import connectStepIcon from "../../../assets/images/marketplace/connect.svg";
import createStepIcon from "../../../assets/images/marketplace/create.svg";

import {
  MainContentArea,
  Hero,
  HeroContent,
  HeroBadge,
  HeroBadgeText,
  HeroTitle,
  HeroSub,
  HeroActions,
  FindEquipmentBtn,
  HireTalentBtn,
  FeaturesSection,
  FeaturesGrid,
  FeatureItem,
  FeatureIconWrap,
  FeatureIcon,
  FeatureTitle,
  FeatureDesc,
  TrendingSection,
  TrendingHeaderRow,
  TrendingHeadingCol,
  TrendingSub,
  TrendingTitle,
  TrendingNav,
  TrendingArrowBtn,
  TrendingGrid,
  TrendingCard,
  TrendingCardImageWrap,
  TrendingCardImage,
  TrendingBadge,
  TrendingCardMeta,
  TrendingCardCategory,
  TrendingCardTitle,
  TrendingCardPrice,
  TalentLinkBtn,
  TalentCard,
  TalentCardImageWrap,
  TalentCardImage,
  TalentOverlay,
  TalentCardRole,
  TalentCardName,
  TalentPillWrap,
  TalentPill,
  TalentQuote,
  HowItWorksSection,
  HowItWorksTitle,
  HowItWorksSub,
  HowItWorksGrid,
  HowItWorksCol,
  HowItWorksIconBox,
  HowItWorksIcon,
  HowItWorksNumberBadge,
  HowItWorksStepTitle,
  HowItWorksStepDesc,
  CtaSection,
  CtaCard,
  CtaTitle,
  CtaDesc,
  CtaButtons,
  CtaPrimaryBtn,
  CtaSecondaryBtn,
  QuoteSection,
  QuoteGrid,
  QuoteCard,
  QuoteTopRow,
  QuoteRefBadge,
  QuoteTypeBadge,
  QuoteTitle,
  QuoteDesc,
  QuoteMetaGrid,
  QuoteMetaChip,
  QuoteFooter,
  QuoteApplyBtn,
  QuoteAppliedPill,
  ModalOverlay,
  ModalBox,
  ModalTopBar,
  ModalHeader,
  ModalTitle,
  ModalClose,
  ModalForm,
  MFieldGroup,
  MFieldLabel,
  MFieldInput,
  MFieldTextarea,
  ModalSubmitBtn,
} from "./MarketplaceBrowsePageStyles";

interface ListingItem {
  id: string;
  title: string;
  price: string | number;
  listing_type: string;
  category_name?: string;
  images?: { image_url: string }[];
}

const MarketplaceBrowsePage: React.FC = () => {
  const navigate = useNavigate();
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<MarketplaceProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [openReqs, setOpenReqs] = useState<MarketplaceRequirement[]>([]);
  const [reqsLoading, setReqsLoading] = useState(true);
  const [appliedReqIds, setAppliedReqIds] = useState<Record<string, boolean>>({});
  
  // Apply Modal state
  const [selectedReq, setSelectedReq] = useState<MarketplaceRequirement | null>(null);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [applyRate, setApplyRate] = useState("");
  const [applyMessage, setApplyMessage] = useState("");
  const [applyCompany, setApplyCompany] = useState("");
  const [submittingApply, setSubmittingApply] = useState(false);

  const [carouselIndex, setCarouselIndex] = useState(0);
  const ITEMS_PER_PAGE = 3;

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPublicListings({
        page: 1,
        page_size: 10,
        ordering: "-created_at",
      });
      setListings(res.results ?? []);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProfiles = useCallback(async () => {
    setProfilesLoading(true);
    try {
      const res = await getPublicProfiles({
        page: 1,
        page_size: 10,
      });
      setProfiles(res.results ?? []);
    } catch {
      setProfiles([]);
    } finally {
      setProfilesLoading(false);
    }
  }, []);

  const fetchOpenReqs = useCallback(async () => {
    setReqsLoading(true);
    try {
      const res = await getOpenRequirements({
        page: 1,
        page_size: 6,
        requirement_type: "bulk_custom_unit",
      });
      setOpenReqs(res.results ?? []);
    } catch {
      setOpenReqs([]);
    } finally {
      setReqsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchListings();
    fetchProfiles();
    fetchOpenReqs();
    mpTrack("marketplace.visit", { path: "/marketplace" });
    sendFirebaseEvent('Marketplace_Page_View', 'Marketplace', 'page_view', 'all');
  }, [fetchListings, fetchProfiles, fetchOpenReqs]);

  const handleApplyClick = (req: MarketplaceRequirement) => {
    if (req.is_owner) {
      navigate("/marketplace/received-applications");
      return;
    }
    const hasToken = !!localStorage.getItem("access_token");
    if (!hasToken) {
      showErrorToast("Please log in first to submit a quote.");
      window.dispatchEvent(new CustomEvent("open-marketplace-login"));
      return;
    }
    if (isAccountFrozen()) {
      showErrorToast("Your marketplace account is frozen or deactivated. You cannot submit quotes.");
      return;
    }
    if (!checkProfileCompleteness()) {
      showErrorToast("Please complete your profile details first to submit a quote.");
      navigate("/marketplace/profile/create");
      return;
    }
    setSelectedReq(req);
    setApplyRate("");
    setApplyMessage("");
    setApplyCompany("");
    setIsApplyModalOpen(true);
  };

  const handleApplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReq) return;
    if (!applyRate || Number(applyRate) <= 0) {
      showErrorToast("Please enter a valid proposed quote rate.");
      return;
    }

    setSubmittingApply(true);
    try {
      await applyToRequirement(selectedReq.id, {
        proposed_rate: Number(applyRate),
        message: applyMessage.trim() || undefined,
        company_name: applyCompany.trim() || undefined,
      });

      setAppliedReqIds(prev => ({ ...prev, [selectedReq.id]: true }));
      setOpenReqs(prev => prev.map(r => r.id === selectedReq.id ? { ...r, has_applied: true } : r));
      sendFirebaseEvent('Marketplace_Application_Submitted', 'Marketplace', 'quote_bid', selectedReq.id);
      showSuccessToast("🎉 Quote / Proposal submitted successfully!");
      setIsApplyModalOpen(false);
    } catch (err: any) {
      const errMsg =
        err?.response?.data?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to submit quote. Please try again.";
      showErrorToast(errMsg);
    } finally {
      setSubmittingApply(false);
    }
  };

  return (
    <>
      <MainContentArea>
        <Hero>
          <HeroContent>
            <HeroBadge>
              <HeroBadgeText>
                <span className="dot">&bull;</span> The Industry Standard
              </HeroBadgeText>
            </HeroBadge>
            <HeroTitle>
              Everything you need to <span className="italic">create.</span><br />
              All in one place.
            </HeroTitle>
            <HeroSub>
              Access the world's most advanced production gear and connect with elite cinematography talent. From indie shorts to global campaigns.
            </HeroSub>
            <HeroActions>
              <FindEquipmentBtn onClick={() => navigate("/marketplace/equipment")}>
                Find Equipment &rarr;
              </FindEquipmentBtn>
              <HireTalentBtn onClick={() => navigate("/marketplace/talent")}>
                Hire Talent
              </HireTalentBtn>
              {/* <HireTalentBtn 
                onClick={() => navigate("/marketplace/dashboard")}
                style={{ background: "rgba(255, 201, 81, 0.12)", borderColor: "rgba(255, 201, 81, 0.3)", color: "#ffc951" }}
              >
                Dashboard
              </HireTalentBtn> */}
            </HeroActions>
          </HeroContent>
        </Hero>

        <FeaturesSection>
          <FeaturesGrid>
            <FeatureItem>
              <FeatureIconWrap>
                <FeatureIcon $maskUrl={buySellRentIcon} />
              </FeatureIconWrap>
              <FeatureTitle>Buy, Sell, or Rent</FeatureTitle>
              <FeatureDesc>
                Complete lifecycle management for your production assets. High liquidity for sellers, flexibility for renters.
              </FeatureDesc>
            </FeatureItem>

            <FeatureItem>
              <FeatureIconWrap>
                <FeatureIcon $maskUrl={verifiedProfessionalsIcon} />
              </FeatureIconWrap>
              <FeatureTitle>Verified Professionals</FeatureTitle>
              <FeatureDesc>
                Every talent profile and gear listing undergoes a rigorous 12-point verification process for your peace of mind.
              </FeatureDesc>
            </FeatureItem>

            <FeatureItem>
              <FeatureIconWrap>
                <FeatureIcon $maskUrl={securePaymentsIcon} />
              </FeatureIconWrap>
              <FeatureTitle>Secure Payments</FeatureTitle>
              <FeatureDesc>
                Enterprise-grade escrow and instant-release payments. Insured transactions up to $250k per rental.
              </FeatureDesc>
            </FeatureItem>
          </FeaturesGrid>
        </FeaturesSection>

        <TrendingSection>
          <TrendingHeaderRow>
            <TrendingHeadingCol>
              <TrendingSub>Marketplace</TrendingSub>
              <TrendingTitle>Trending Equipment</TrendingTitle>
            </TrendingHeadingCol>
            <TrendingNav>
              <TrendingArrowBtn
                aria-label="Previous"
                disabled={carouselIndex === 0}
                onClick={() => setCarouselIndex(i => Math.max(0, i - 1))}
              >
                <ChevronLeft size={20} />
              </TrendingArrowBtn>
              <TrendingArrowBtn
                aria-label="Next"
                disabled={carouselIndex + ITEMS_PER_PAGE >= listings.length}
                onClick={() => setCarouselIndex(i => Math.min(listings.length - ITEMS_PER_PAGE, i + 1))}
              >
                <ChevronRight size={20} />
              </TrendingArrowBtn>
            </TrendingNav>
          </TrendingHeaderRow>

          <TrendingGrid>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                  <div style={{ width: "100%", aspectRatio: "4/3.8", borderRadius: "24px", background: "var(--mp-cardBackground)", opacity: 0.5 }} />
                  <div style={{ height: "14px", width: "30%", background: "var(--mp-cardBackground)", borderRadius: "4px" }} />
                  <div style={{ height: "20px", width: "70%", background: "var(--mp-cardBackground)", borderRadius: "4px" }} />
                  <div style={{ height: "16px", width: "40%", background: "var(--mp-cardBackground)", borderRadius: "4px" }} />
                </div>
              ))
            ) : listings.slice(carouselIndex, carouselIndex + ITEMS_PER_PAGE).map(item => {
              const coverImage = item.images && item.images.length > 0 ? resolveImage(item.images[0].image_url) : "";
              return (
                <TrendingCard key={item.id} onClick={() => navigate(`/marketplace/listing/${item.id}`)}>
                  <TrendingCardImageWrap>
                    {coverImage ? (
                      <TrendingCardImage src={coverImage} alt={item.title} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", background: "var(--mp-cardBackground)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Camera size={40} color="var(--mp-heroSubColor)" />
                      </div>
                    )}
                  </TrendingCardImageWrap>
                  <TrendingCardMeta>
                    <TrendingCardCategory>{item.category_name || "Equipment"}</TrendingCardCategory>
                    <TrendingCardTitle>{item.title}</TrendingCardTitle>
                    <TrendingCardPrice>
                      <span className="amount">{formatINR(item.price)}</span>
                      <span className="unit">{item.listing_type === 'rent' ? '/ day' : ''}</span>
                    </TrendingCardPrice>
                  </TrendingCardMeta>
                </TrendingCard>
              );
            })}
          </TrendingGrid>
        </TrendingSection>

        {/* Open Quote & Custom Unit Requests Section */}
        <QuoteSection>
          <TrendingHeaderRow>
            <TrendingHeadingCol>
              <TrendingSub>Live Production Requests</TrendingSub>
              <TrendingTitle>Open Quotes &amp; Custom Unit Requests</TrendingTitle>
            </TrendingHeadingCol>
            <TrendingNav>
              <TalentLinkBtn onClick={() => navigate("/marketplace/team")}>
                Build Custom Unit &rarr;
              </TalentLinkBtn>
            </TrendingNav>
          </TrendingHeaderRow>

          {reqsLoading ? (
            <QuoteGrid>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ height: "240px", borderRadius: "20px", background: "var(--mp-cardBackground, #ffffff)", opacity: 0.5 }} />
              ))}
            </QuoteGrid>
          ) : openReqs.length === 0 ? (
            <div style={{
              background: "var(--mp-cardBackground, #ffffff)",
              border: "1px solid var(--mp-border, rgba(23, 23, 23, 0.08))",
              borderRadius: "20px",
              padding: "48px 24px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "12px",
              marginTop: "24px"
            }}>
              <Briefcase size={40} color="#c94daa" style={{ opacity: 0.6 }} />
              <h4 style={{ margin: 0, fontFamily: "Liberation Serif, serif", fontSize: "18px", color: "var(--mp-headingColor, #171717)" }}>
                No open quote requests right now
              </h4>
              <p style={{ margin: 0, fontSize: "13px", color: "var(--mp-featureDescColor, #666)", maxWidth: "400px" }}>
                Need a dedicated crew, custom unit, or gear package for your upcoming production? Post a quote request.
              </p>
              <TalentLinkBtn onClick={() => navigate("/marketplace/team")} style={{ marginTop: "8px" }}>
                Build Your Custom Unit
              </TalentLinkBtn>
            </div>
          ) : (
            <QuoteGrid>
              {openReqs.map(req => {
                const isApplied = req.has_applied || appliedReqIds[req.id];
                const projectLabel = (req as any).project_type_label || (
                  req.project_type
                    ? req.project_type.replace(/_/g, " ")
                    : req.requirement_type === "bulk_custom_unit"
                    ? "Custom Unit"
                    : req.requirement_type === "equipment_requirement"
                    ? "Equipment"
                    : "Service"
                );

                const budgetDisplay = (req as any).budget_range_label || (
                  req.budget_range
                    ? req.budget_range.replace(/_/g, " ").toUpperCase()
                    : req.budget
                    ? formatINR(req.budget)
                    : "Competitive"
                );

                return (
                  <QuoteCard key={req.id}>
                    <QuoteTopRow>
                      <QuoteRefBadge>
                        {req.reference || `QT-${req.id.slice(0, 6).toUpperCase()}`}
                      </QuoteRefBadge>
                      <QuoteTypeBadge>{projectLabel}</QuoteTypeBadge>
                    </QuoteTopRow>

                    <QuoteTitle>{req.title || "Custom Unit Production"}</QuoteTitle>

                    {req.description && (
                      <QuoteDesc>{req.description}</QuoteDesc>
                    )}

                    <QuoteMetaGrid>
                      {req.location && (
                        <QuoteMetaChip>
                          <MapPin size={13} /> {req.location}
                        </QuoteMetaChip>
                      )}
                      {req.date_from && (
                        <QuoteMetaChip>
                          <Calendar size={13} /> {formatDate(req.date_from)}
                          {req.date_to ? ` → ${formatDate(req.date_to)}` : ""}
                        </QuoteMetaChip>
                      )}
                      <QuoteMetaChip>
                        <DollarSign size={13} /> {budgetDisplay}
                      </QuoteMetaChip>
                      {req.headcount ? (
                        <QuoteMetaChip>
                          <Users size={13} /> {req.headcount} Crew
                        </QuoteMetaChip>
                      ) : (
                        <QuoteMetaChip>
                          <Users size={13} /> {req.applicant_count ?? 0} Bids
                        </QuoteMetaChip>
                      )}
                    </QuoteMetaGrid>

                    <QuoteFooter>
                      {req.is_owner ? (
                        <QuoteAppliedPill
                          style={{
                            background: "rgba(201, 77, 170, 0.12)",
                            color: "#c94daa",
                            border: "1px solid rgba(201, 77, 170, 0.3)",
                            cursor: "pointer",
                          }}
                          onClick={() => navigate("/marketplace/received-applications")}
                        >
                          <Users size={13} /> Your Request • View Bids
                        </QuoteAppliedPill>
                      ) : isApplied ? (
                        <QuoteAppliedPill>
                          <CheckCircle2 size={14} /> Applied ✓
                        </QuoteAppliedPill>
                      ) : (
                        <QuoteApplyBtn onClick={() => handleApplyClick(req)}>
                          <Send size={13} /> Submit Quote / Apply
                        </QuoteApplyBtn>
                      )}
                    </QuoteFooter>
                  </QuoteCard>
                );
              })}
            </QuoteGrid>
          )}
        </QuoteSection>

        <TrendingSection style={{ paddingTop: 0 }}>
          <TrendingHeaderRow>
            <TrendingHeadingCol>
              <TrendingSub>The Network</TrendingSub>
              <TrendingTitle>Elite Talent for your next production</TrendingTitle>
            </TrendingHeadingCol>
            <TrendingNav>
              <TalentLinkBtn onClick={() => navigate("/marketplace?tab=talent")}>
                View All Professionals
              </TalentLinkBtn>
            </TrendingNav>
          </TrendingHeaderRow>

          <TrendingGrid>
            {profilesLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div style={{ width: "100%", aspectRatio: "4/4.4", borderRadius: "24px", background: "var(--mp-cardBackground)", opacity: 0.5 }} />
                  <div style={{ height: "14px", width: "40%", background: "var(--mp-cardBackground)", borderRadius: "4px" }} />
                  <div style={{ height: "16px", width: "80%", background: "var(--mp-cardBackground)", borderRadius: "4px" }} />
                </div>
              ))
            ) : profiles.slice(0, 3).map(profile => {
              const avatarImage = profile.profile_picture ? resolveImage(profile.profile_picture) : "";
              const profileRole = profile.profession || profile.profile_type;
              const isStudio = profile.profile_type?.toLowerCase() === "studio" || profile.professional_type?.toLowerCase() === "studio";

              let workCategories: string[] = [];
              if (Array.isArray(profile.work_categories) && profile.work_categories.length > 0) {
                workCategories = profile.work_categories;
              } else if (typeof profile.work_categories === "string" && (profile.work_categories as string).trim()) {
                workCategories = (profile.work_categories as string).split(",").map(s => s.trim());
              } else if (Array.isArray(profile.skills) && profile.skills.length > 0) {
                workCategories = profile.skills;
              } else if (typeof profile.skills === "string" && (profile.skills as string).trim()) {
                workCategories = (profile.skills as string).split(",").map(s => s.trim());
              }
              workCategories = workCategories.filter(c => Boolean(c && String(c).trim()));

              const hasCity = Boolean(profile.city && profile.city.trim());
              const hasExp = profile.experience_years != null && String(profile.experience_years).trim() !== "";
              const hasTeamSize = profile.team_size != null && String(profile.team_size).trim() !== "";

              const portfolio = Array.isArray(profile.portfolio)
                ? profile.portfolio.filter(p => p && (p.url || (p as any).image_url))
                : [];

              return (
                <TalentCard key={profile.id} onClick={() => navigate(`/marketplace/profile/${profile.id}`)}>
                  <TalentCardImageWrap>
                    {avatarImage ? (
                      <TalentCardImage src={avatarImage} alt={profile.display_name} />
                    ) : (
                      <div style={{ width: "100%", height: "100%", background: "var(--mp-cardBackground, #f0f0f0)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Camera size={40} color="var(--mp-heroSubColor, #888888)" />
                      </div>
                    )}
                    <TalentOverlay>
                      <TalentCardRole>{profileRole}</TalentCardRole>
                      <TalentCardName>{profile.display_name}</TalentCardName>
                    </TalentOverlay>
                  </TalentCardImageWrap>

                  {/* Work Categories Badges Below Image */}
                  {workCategories.length > 0 && (
                    <TalentPillWrap>
                      {workCategories.slice(0, 4).map((cat, idx) => (
                        <TalentPill key={idx} style={{ borderRadius: "20px", padding: "4px 12px" }}>
                          {cat}
                        </TalentPill>
                      ))}
                    </TalentPillWrap>
                  )}

                  {/* Horizontal Line 1 */}
                  <div style={{ width: "100%", height: "1px", background: "var(--mp-borderColor, rgba(255, 255, 255, 0.1))", opacity: 0.5 }} />

                  {/* City & Exp (Freelancer) OR City & Team Size (Studio) */}
                  {(hasCity || (isStudio ? hasTeamSize : hasExp)) && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 4px" }}>
                      {hasCity && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                          <span style={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--mp-featureDescColor, #434655)", opacity: 0.6 }}>CITY</span>
                          <span style={{ fontFamily: "Liberation Serif, serif", fontSize: "15px", fontWeight: 700, color: "var(--mp-headingColor, #171717)" }}>{profile.city}</span>
                        </div>
                      )}
                      {isStudio ? (
                        hasTeamSize && (
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px", textAlign: hasCity ? "right" : "left" }}>
                            <span style={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--mp-featureDescColor, #434655)", opacity: 0.6 }}>TEAM SIZE</span>
                            <span style={{ fontFamily: "Liberation Serif, serif", fontSize: "15px", fontWeight: 700, color: "var(--mp-headingColor, #171717)" }}>{profile.team_size} Members</span>
                          </div>
                        )
                      ) : (
                        hasExp && (
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px", textAlign: hasCity ? "right" : "left" }}>
                            <span style={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--mp-featureDescColor, #434655)", opacity: 0.6 }}>EXP</span>
                            <span style={{ fontFamily: "Liberation Serif, serif", fontSize: "15px", fontWeight: 700, color: "var(--mp-headingColor, #171717)" }}>{profile.experience_years} Yrs</span>
                          </div>
                        )
                      )}
                    </div>
                  )}

                  {/* Horizontal Line 2 & Portfolio Preview (only if non-empty) */}
                  {portfolio.length > 0 && (
                    <>
                      <div style={{ width: "100%", height: "1px", background: "var(--mp-borderColor, rgba(255, 255, 255, 0.1))", opacity: 0.5 }} />
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                        <span style={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.8px", textTransform: "uppercase", color: "var(--mp-featureDescColor, #434655)", opacity: 0.6 }}>PORTFOLIO PREVIEW</span>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                          {portfolio.slice(0, 3).map((item, idx) => (
                            <div key={idx} style={{ aspectRatio: "1", borderRadius: "12px", overflow: "hidden", background: "rgba(0,0,0,0.1)" }}>
                              <img src={resolveImage(item.url || (item as any).image_url)} alt={item.title || "Portfolio"} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </TalentCard>
              );
            })}
          </TrendingGrid>
        </TrendingSection>

        <HowItWorksSection>
          <HowItWorksTitle>How it Works</HowItWorksTitle>
          <HowItWorksSub>Getting your production off the ground in three simple steps.</HowItWorksSub>
          
          <HowItWorksGrid>
            <HowItWorksCol>
              <HowItWorksNumberBadge>01</HowItWorksNumberBadge>
              <HowItWorksIconBox>
                <HowItWorksIcon $maskUrl={discoverStepIcon} />
              </HowItWorksIconBox>
              <HowItWorksStepTitle>Discover</HowItWorksStepTitle>
              <HowItWorksStepDesc>
                Browse thousands of verified listings for the specific gear or talent your project requires.
              </HowItWorksStepDesc>
            </HowItWorksCol>

            <HowItWorksCol>
              <HowItWorksNumberBadge>02</HowItWorksNumberBadge>
              <HowItWorksIconBox>
                <HowItWorksIcon $maskUrl={connectStepIcon} />
              </HowItWorksIconBox>
              <HowItWorksStepTitle>Connect &amp; Book</HowItWorksStepTitle>
              <HowItWorksStepDesc>
                Secure your booking through our protected payment system with instant contract generation.
              </HowItWorksStepDesc>
            </HowItWorksCol>

            <HowItWorksCol>
              <HowItWorksNumberBadge>03</HowItWorksNumberBadge>
              <HowItWorksIconBox>
                <HowItWorksIcon $maskUrl={createStepIcon} />
              </HowItWorksIconBox>
              <HowItWorksStepTitle>Create</HowItWorksStepTitle>
              <HowItWorksStepDesc>
                Focus on your vision. Pick up your gear or meet your team and start producing exceptional work.
              </HowItWorksStepDesc>
            </HowItWorksCol>
          </HowItWorksGrid>
        </HowItWorksSection>

        <CtaSection>
          <CtaCard>
            <CtaTitle>Ready to bring your next big idea to life?</CtaTitle>
            <CtaDesc>
              Join 50,000+ industry professionals already growing their creative business on our marketplace.
            </CtaDesc>
            <CtaButtons>
              <CtaPrimaryBtn onClick={() => navigate("/marketplace/sell")}>
                Get Started Free
              </CtaPrimaryBtn>
              <CtaSecondaryBtn onClick={() => navigate("/extra/refund-register")}>
                Contact Sales
              </CtaSecondaryBtn>
            </CtaButtons>
          </CtaCard>
        </CtaSection>
      </MainContentArea>

      {/* Apply / Bid Modal */}
      {isApplyModalOpen && selectedReq && (
        <ModalOverlay onClick={() => !submittingApply && setIsApplyModalOpen(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTopBar />
            <ModalHeader>
              <div>
                <ModalTitle>Submit Quote / Bid</ModalTitle>
                <span style={{ fontSize: "12px", color: "rgba(23, 23, 23, 0.5)", fontFamily: "Montserrat, sans-serif" }}>
                  Send your proposal directly to the requester
                </span>
              </div>
              <ModalClose onClick={() => !submittingApply && setIsApplyModalOpen(false)} title="Close">
                <X size={18} />
              </ModalClose>
            </ModalHeader>

            <ModalForm onSubmit={handleApplySubmit}>
              {/* Requirement Summary Box */}
              <div style={{
                background: "rgba(201, 77, 170, 0.04)",
                border: "1px solid rgba(201, 77, 170, 0.15)",
                borderRadius: "14px",
                padding: "12px 14px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                  <span style={{ fontFamily: "Liberation Serif, serif", fontWeight: 700, fontSize: "15px", color: "#171717" }}>
                    {selectedReq.title || "Custom Unit Requirement"}
                  </span>
                  <span style={{ fontSize: "11px", fontWeight: 700, color: "#c94daa", background: "rgba(201, 77, 170, 0.12)", padding: "2px 8px", borderRadius: "999px" }}>
                    {selectedReq.reference || `QT-${selectedReq.id.slice(0, 6).toUpperCase()}`}
                  </span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", fontSize: "12px", color: "rgba(23, 23, 23, 0.65)", fontFamily: "Urbanist, sans-serif" }}>
                  {selectedReq.location && <span>📍 {selectedReq.location}</span>}
                  {selectedReq.date_from && <span>📅 {formatDate(selectedReq.date_from)}</span>}
                  {selectedReq.budget_range && <span>💰 {selectedReq.budget_range.replace(/_/g, " ").toUpperCase()}</span>}
                </div>
              </div>

              <MFieldGroup>
                <MFieldLabel>Proposed Quote / Rate (₹) *</MFieldLabel>
                <MFieldInput
                  type="number"
                  placeholder="e.g. 25000"
                  value={applyRate}
                  onChange={(e) => setApplyRate(e.target.value)}
                  required
                  min="1"
                  autoFocus
                />
              </MFieldGroup>

              <MFieldGroup>
                <MFieldLabel>Studio / Company Name (Optional)</MFieldLabel>
                <MFieldInput
                  type="text"
                  placeholder="e.g. Lens Studio"
                  value={applyCompany}
                  onChange={(e) => setApplyCompany(e.target.value)}
                />
              </MFieldGroup>

              <MFieldGroup>
                <MFieldLabel>Proposal Message / Pitch (Optional)</MFieldLabel>
                <MFieldTextarea
                  placeholder="Describe your equipment, team setup, or portfolio highlights..."
                  value={applyMessage}
                  onChange={(e) => setApplyMessage(e.target.value)}
                  rows={3}
                />
              </MFieldGroup>

              <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
                <button
                  type="button"
                  onClick={() => setIsApplyModalOpen(false)}
                  disabled={submittingApply}
                  style={{
                    flex: 1,
                    height: "44px",
                    border: "1px solid rgba(23, 23, 23, 0.15)",
                    borderRadius: "10px",
                    background: "transparent",
                    color: "#171717",
                    fontFamily: "Urbanist, sans-serif",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Cancel
                </button>
                <ModalSubmitBtn type="submit" disabled={submittingApply} style={{ flex: 1.5, margin: 0 }}>
                  {submittingApply ? "Submitting Bid…" : "Submit Quote"}
                </ModalSubmitBtn>
              </div>
            </ModalForm>
          </ModalBox>
        </ModalOverlay>
      )}
    </>
  );
};

export default MarketplaceBrowsePage;
